/**
 * SlaTargetsPage — System · SLA Targets.
 *
 * Per-(origin × stage) SLA target with an alert threshold percentage.
 * Composite primary key on (OriginCode, StageCode); the read endpoint
 * also returns the allowed Origin + Stage lookup catalogs so the editor
 * can offer dropdowns instead of raw text input.
 *
 * Contract derived from primary sources (2026-05-18):
 *   - Entity:    PCBJxChangeApi/Data/Entities/Config/Codes/SlaTarget.cs
 *   - Lookups:   SlaStageDefinition + SlaOriginDefinition (same dir)
 *   - Read:      ConfigFunction.cs:803-824 returns
 *                  { success:true, data:{ targets, stageDefinitions, originDefinitions } }
 *   - Write:     AdminSystemFunction.cs:181 POST /v3/admin/sla-targets
 *   - Delete:    AdminSystemFunction.cs:204 DELETE /v3/admin/sla-targets/{originCode}/{stageCode}
 *   - DTO:       SlaTargetUpsertInput (originCode/stageCode/displayName/
 *                  targetSeconds/alertThresholdPercent/isActive)
 *
 * NOT a port of any sibling editor. The shared-types `SlaTarget` interface
 * at `packages/shared-types/src/config/codes.ts:417` has stale field names
 * (`targetId`/`stageName`/`originKey`/`targetMinutes`/`escalationMinutes`)
 * that don't match the live wire — same trap as SystemSetting in the
 * same file. The api-client `slaTargets()` method is also mis-typed (returns
 * an array shape; the wire actually returns an object with three keys).
 * The page declares its own row + lookup types matching live behavior.
 *
 * Auth: MSAL Path B via ADMIN_AUTH_TOKEN_SENTINEL (no bypass-key prompt;
 * bypass-key gated OFF in prod).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type SlaTargetUpsertBody,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, NumberField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface SlaTargetRow {
  originCode: string;
  stageCode: string;
  displayName: string;
  targetSeconds: number;
  alertThresholdPercent: number;
  isActive: boolean;
}

interface CatalogEntry {
  code: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  sortOrder?: number | null;
}

interface SlaTargetsPayload {
  targets: ReadonlyArray<SlaTargetRow>;
  stageDefinitions: ReadonlyArray<CatalogEntry>;
  originDefinitions: ReadonlyArray<CatalogEntry>;
}

interface EditDraft {
  isNew: boolean;
  originCode: string;
  stageCode: string;
  displayName: string;
  targetSeconds: number;
  alertThresholdPercent: number;
  isActive: boolean;
  /** When editing an existing row, the (originCode, stageCode) pair is
   * read-only — the composite key cannot change via upsert. */
  lockedKey: boolean;
}

function emptyDraft(): EditDraft {
  return {
    isNew: true,
    originCode: '',
    stageCode: '',
    displayName: '',
    targetSeconds: 0,
    alertThresholdPercent: 80,
    isActive: true,
    lockedKey: false,
  };
}

function rowToDraft(row: SlaTargetRow): EditDraft {
  return {
    isNew: false,
    originCode: row.originCode,
    stageCode: row.stageCode,
    displayName: row.displayName,
    targetSeconds: row.targetSeconds,
    alertThresholdPercent: row.alertThresholdPercent,
    isActive: row.isActive,
    lockedKey: true,
  };
}

function draftToUpsertBody(d: EditDraft): SlaTargetUpsertBody {
  return {
    originCode: d.originCode.trim(),
    stageCode: d.stageCode.trim(),
    displayName: d.displayName.trim(),
    targetSeconds: d.targetSeconds,
    alertThresholdPercent: d.alertThresholdPercent,
    isActive: d.isActive,
  };
}

function formatSeconds(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`.replace(' 0s', '');
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function SlaTargetsPage(): JSX.Element {
  const client = useApiClient();
  const [data, setData] = useState<SlaTargetsPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setData(null);
      setLoadError(null);
      try {
        // The api-client's slaTargets() is typed `Promise<ApiResult<
        // ReadonlyArray<SlaTarget>>>` but the wire actually returns an
        // object with three keys (targets / stageDefinitions /
        // originDefinitions). Cast through unknown to the correct shape.
        const raw = (await client.retail.config.slaTargets()) as unknown as {
          success: boolean;
          error?: string;
          data?: SlaTargetsPayload;
        };
        if (cancelled) return;
        if (raw.success && raw.data) {
          setData(raw.data);
        } else {
          setLoadError(raw.error ?? 'Failed to load SLA targets.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const filteredTargets = useMemo(() => {
    if (!data) return null;
    const n = filter.trim().toLowerCase();
    if (!n) return data.targets;
    return data.targets.filter((t) => {
      const hay = `${t.originCode} ${t.stageCode} ${t.displayName}`.toLowerCase();
      return hay.includes(n);
    });
  }, [data, filter]);

  const originOptions = useMemo(
    () =>
      (data?.originDefinitions ?? [])
        .filter((o) => o.isActive)
        .map((o) => ({ value: o.code, label: `${o.code} — ${o.name}` })),
    [data],
  );

  const stageOptions = useMemo(
    () =>
      (data?.stageDefinitions ?? [])
        .filter((s) => s.isActive)
        .map((s) => ({ value: s.code, label: `${s.code} — ${s.name}` })),
    [data],
  );

  const columns: ReadonlyArray<DataTableColumn<SlaTargetRow>> = useMemo(
    () => [
      {
        header: 'Origin',
        cell: (r) => <code className="cell-mono--bold">{r.originCode}</code>,
      },
      {
        header: 'Stage',
        cell: (r) => <code className="cell-mono--bold">{r.stageCode}</code>,
      },
      {
        header: 'Display name',
        cell: (r) => r.displayName,
      },
      {
        header: 'Target',
        cell: (r) => <span className="cell-mono">{formatSeconds(r.targetSeconds)}</span>,
      },
      {
        header: 'Alert at',
        cell: (r) => <span className="cell-mono">{r.alertThresholdPercent}%</span>,
      },
      {
        header: 'Status',
        cell: (r) =>
          r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
      },
      {
        header: '',
        cell: (r) => <Button onClick={() => setDraft(rowToDraft(r))}>Edit</Button>,
      },
    ],
    [],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.originCode.trim()) {
      setSaveError('Origin is required.');
      return;
    }
    if (!draft.stageCode.trim()) {
      setSaveError('Stage is required.');
      return;
    }
    if (!draft.displayName.trim()) {
      setSaveError('Display name is required.');
      return;
    }
    if (draft.targetSeconds <= 0) {
      setSaveError('Target must be a positive number of seconds.');
      return;
    }
    if (draft.alertThresholdPercent < 1 || draft.alertThresholdPercent > 100) {
      setSaveError('Alert threshold must be between 1 and 100 percent.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.slaTargets.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToUpsertBody(draft),
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'admin upsert returned success=false');
        return;
      }
      setDraft(null);
      setToast({
        tone: 'info',
        text: draft.isNew
          ? `Created SLA target ${draft.originCode}/${draft.stageCode}.`
          : `Updated SLA target ${draft.originCode}/${draft.stageCode}.`,
      });
      refresh();
    } catch (e) {
      setSaveError(
        ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
      );
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  const handleDelete = useCallback(async () => {
    if (!draft || draft.isNew) return;
    const oc = draft.originCode.trim();
    const sc = draft.stageCode.trim();
    if (!oc || !sc) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.slaTargets.remove(ADMIN_AUTH_TOKEN_SENTINEL, oc, sc);
      if (!resp.success) {
        setSaveError(resp.error ?? 'delete returned success=false');
        return;
      }
      setDraft(null);
      setToast({ tone: 'warn', text: `Deleted SLA target ${oc}/${sc}.` });
      refresh();
    } catch (e) {
      setSaveError(
        ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
      );
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load SLA targets: {loadError}{' '}
        <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!data) return <Spinner label="Loading SLA targets…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`SLA Targets — ${filteredTargets?.length ?? 0} of ${data.targets.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField
                label=""
                value={filter}
                onChange={setFilter}
                placeholder="Filter by origin, stage, or name…"
              />
            </div>
            <Button variant="primary" onClick={() => setDraft(emptyDraft())}>
              + Add Target
            </Button>
          </div>
        }
      >
        <DataTable<SlaTargetRow>
          columns={columns}
          rows={filteredTargets ?? []}
          rowKey={(r) => `${r.originCode}|${r.stageCode}`}
          emptyState={<p className="empty">No SLA targets match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={
          draft?.isNew
            ? 'Add SLA Target'
            : `Edit ${draft?.originCode}/${draft?.stageCode}`
        }
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={560}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              {!draft.isNew && (
                <Button onClick={() => void handleDelete()} disabled={saving}>
                  Delete
                </Button>
              )}
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <SelectField
              label="Origin"
              value={draft.originCode}
              onChange={(v) => setDraft({ ...draft, originCode: v })}
              options={
                draft.lockedKey
                  ? [{ value: draft.originCode, label: draft.originCode }]
                  : originOptions
              }
              placeholder={draft.lockedKey ? undefined : 'Choose an origin…'}
              disabled={draft.lockedKey}
              hint={
                draft.lockedKey
                  ? 'Composite key is read-only on existing rows. Delete and re-create to re-key.'
                  : 'Where the stage timer starts. Sourced from SlaOriginDefinitions.'
              }
            />

            <SelectField
              label="Stage"
              value={draft.stageCode}
              onChange={(v) => setDraft({ ...draft, stageCode: v })}
              options={
                draft.lockedKey
                  ? [{ value: draft.stageCode, label: draft.stageCode }]
                  : stageOptions
              }
              placeholder={draft.lockedKey ? undefined : 'Choose a stage…'}
              disabled={draft.lockedKey}
              hint={
                draft.lockedKey
                  ? undefined
                  : 'The pipeline stage being measured. Sourced from SlaStageDefinitions.'
              }
            />

            <TextField
              label="Display name"
              value={draft.displayName}
              onChange={(v) => setDraft({ ...draft, displayName: v })}
              placeholder="Short label surfaced in dashboards."
            />

            <NumberField
              label="Target (seconds)"
              value={draft.targetSeconds}
              onChange={(v) => setDraft({ ...draft, targetSeconds: v ?? 0 })}
              min={1}
              hint="Total time allowed for this stage. Stored as integer seconds."
            />

            <NumberField
              label="Alert threshold (%)"
              value={draft.alertThresholdPercent}
              onChange={(v) => setDraft({ ...draft, alertThresholdPercent: v ?? 80 })}
              min={1}
              max={100}
              hint="Operations dashboards flag a stage once elapsed time reaches this percentage of the target. 80% is the default."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="When inactive, the target is excluded from dashboard rollups but the row stays for audit."
            />
          </div>
        )}
      </ModalShell>
    </>
  );
}
